"use client";

import { c as _c } from "react/compiler-runtime";
import { compileSpecStream } from "@json-render/core";
import { JSONUIProvider, Renderer } from "@json-render/react";
import { useQuery } from "@tanstack/react-query";
import { Code, Download, FileImage, FileText, LayoutTemplate, Loader2, PenTool, Play, Square } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { memo, type RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { PromptInput, PromptInputBody, PromptInputButton, PromptInputSubmit, PromptInputTextarea, PromptInputToolbar, PromptInputTools } from "@/components/ai-elements/prompt-input";
import { ModelSelector } from "@/components/model-selector";
import { AuthDialog } from "@/components/playground/auth-dialog";
import { CanvasSidebar } from "@/components/playground/canvas-sidebar";
import { NewProjectDialog } from "@/components/playground/new-project-dialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUser } from "@/hooks/useUser";
import { registry } from "@/lib/canvas/registry-web";
import { canvasSuggestions, sampleSuggestions } from "@/lib/hero-suggestions";
import { CANVAS_MODEL_COOKIE, getModelPreferenceCookie, setModelPreferenceCookie } from "@/lib/model-preferences";
import { getErrorMessage } from "@/lib/utils";
import { emptySpec, templates } from "@llmgateway/canvas/templates";
import type { ApiModel, ApiProvider } from "@/lib/fetch-models";
import type { Organization, Project } from "@/lib/types";
import type { Spec } from "@json-render/core";
interface CanvasPageClientProps {
  models: ApiModel[];
  providers: ApiProvider[];
  organizations: Organization[];
  selectedOrganization: Organization | null;
  projects: Project[];
  selectedProject: Project | null;
  projectsLoadFailed: boolean;
  initialModelPreference?: string | null;
}
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
function isRenderableSpec(value: unknown): value is Spec {
  if (!isRecord(value)) {
    return false;
  }
  const elements = value.elements;
  return Boolean(value.root) && isRecord(elements) && Object.keys(elements).length > 0;
}
const DEFAULT_CANVAS_MODEL = "anthropic/claude-sonnet-4-20250514";
const EMPTY_SPEC_JSON = JSON.stringify(emptySpec, null, 2);
const EMPTY_SPEC_COMPACT_JSON = JSON.stringify(emptySpec);
function CanvasSpecSkeleton() {
  const $ = _c(10);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="h-3 w-20 rounded-sm" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="space-y-2 pl-4"><Skeleton className="h-3 w-32 rounded-sm" /><Skeleton className="h-3 w-48 rounded-sm" /><Skeleton className="h-3 w-28 rounded-sm" /></div>;
    t2 = <Skeleton className="h-3 w-24 rounded-sm" />;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="space-y-2 pl-4"><Skeleton className="h-3 w-56 rounded-sm" /><Skeleton className="h-3 w-44 rounded-sm" /><Skeleton className="h-3 w-64 rounded-sm" /><Skeleton className="h-3 w-36 rounded-sm" /></div>;
    t4 = <Skeleton className="h-3 w-28 rounded-sm" />;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="space-y-2 pl-4"><Skeleton className="h-3 w-48 rounded-sm" /><Skeleton className="h-3 w-72 rounded-sm" /><Skeleton className="h-3 w-52 rounded-sm" /><Skeleton className="h-3 w-60 rounded-sm" /></div>;
    t6 = <Skeleton className="h-3 w-24 rounded-sm" />;
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  let t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="space-y-2 pl-8"><Skeleton className="h-3 w-44 rounded-sm" /><Skeleton className="h-3 w-56 rounded-sm" /><Skeleton className="h-3 w-36 rounded-sm" /></div>;
    t8 = <Skeleton className="h-3 w-16 rounded-sm" />;
    $[7] = t7;
    $[8] = t8;
  } else {
    t7 = $[7];
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="flex h-full min-h-[280px] flex-col gap-3 p-4">{t0}{t1}{t2}{t3}{t4}{t5}{t6}{t7}{t8}<div className="space-y-2 pl-4"><Skeleton className="h-3 w-64 rounded-sm" /><Skeleton className="h-3 w-40 rounded-sm" /><Skeleton className="h-3 w-72 rounded-sm" /></div><Skeleton className="h-3 w-12 rounded-sm" /></div>;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  return t9;
}
function CanvasPreviewSkeleton() {
  const $ = _c(5);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="h-10 w-2/5" />;
    t1 = <Skeleton className="h-4 w-3/5" />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="grid gap-4 sm:grid-cols-3"><Skeleton className="h-28" /><Skeleton className="h-28" /><Skeleton className="h-28" /></div>;
    t3 = <Skeleton className="h-52 w-full" />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="mx-auto flex w-full max-w-4xl flex-col gap-5">{t0}{t1}{t2}{t3}<div className="grid gap-4 sm:grid-cols-2"><Skeleton className="h-32" /><Skeleton className="h-32" /></div></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
function CanvasEmptyState(t0) {
  const $ = _c(11);
  const {
    onSuggestionClick,
    disabled
  } = t0;
  const [suggestions, setSuggestions] = useState(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setSuggestions(sampleSuggestions(canvasSuggestions, 6));
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <PenTool className="h-16 w-16 text-muted-foreground/30 mb-6" />;
    t4 = <h3 className="text-lg font-medium mb-2">No canvas yet</h3>;
    t5 = <p className="text-sm text-muted-foreground mb-8 max-w-md">Describe the UI you want to build and watch it render live, or pick a template above.</p>;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t3 = $[2];
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] !== disabled || $[6] !== onSuggestionClick || $[7] !== suggestions) {
    t6 = suggestions ? <motion.div key="suggestions" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.07,
      ease: "easeOut"
    }} className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl w-full">{suggestions.map((s, index) => <motion.button key={s} type="button" initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.12,
        delay: index * 0.025,
        ease: "easeOut"
      }} disabled={disabled} onClick={() => onSuggestionClick(s)} className="rounded-md border px-4 py-3 text-left text-sm hover:bg-muted/60 transition-colors">{s}</motion.button>)}</motion.div> : null;
    $[5] = disabled;
    $[6] = onSuggestionClick;
    $[7] = suggestions;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t6) {
    t7 = <div className="flex h-full flex-col items-center justify-center py-20 text-center">{t3}{t4}{t5}<AnimatePresence>{t6}</AnimatePresence></div>;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
}
async function getResponseErrorMessage(response: Response): Promise<string> {
  const fallback = `HTTP ${response.status}: ${response.statusText}`;
  const text = await response.text().catch(() => "");
  if (!text.trim()) {
    return fallback;
  }
  try {
    const payload: unknown = JSON.parse(text);
    const message = getErrorMessage(payload);
    return message === "An unknown error occurred" ? fallback : message;
  } catch {
    const message = getErrorMessage(text);
    return message === "An unknown error occurred" ? fallback : message;
  }
}
interface CanvasPromptInputProps {
  isGenerating: boolean;
  disabled: boolean;
  onGenerate: (prompt: string) => void;
  onStop: () => void;
  promptRef: RefObject<HTMLTextAreaElement | null>;
}
const CanvasPromptInput = memo(function CanvasPromptInput(t0) {
  const $ = _c(19);
  const {
    isGenerating,
    disabled,
    onGenerate,
    onStop,
    promptRef
  } = t0;
  const [prompt, setPrompt] = useState("");
  let t1;
  if ($[0] !== onGenerate || $[1] !== prompt) {
    t1 = () => {
      onGenerate(prompt);
    };
    $[0] = onGenerate;
    $[1] = prompt;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleSubmit = t1;
  const t2 = isGenerating || disabled;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e => setPrompt(e.currentTarget.value);
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = isGenerating || disabled;
  let t5;
  if ($[4] !== prompt || $[5] !== promptRef || $[6] !== t4) {
    t5 = <PromptInputBody><PromptInputTextarea ref={promptRef} value={prompt} onChange={t3} placeholder="Describe the UI you want to build..." disabled={t4} /></PromptInputBody>;
    $[4] = prompt;
    $[5] = promptRef;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <PromptInputTools />;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== disabled || $[10] !== isGenerating || $[11] !== onStop || $[12] !== prompt) {
    t7 = <PromptInputToolbar>{t6}{isGenerating ? <PromptInputButton onClick={onStop} variant="ghost"><Square className="h-3.5 w-3.5" /></PromptInputButton> : <PromptInputSubmit disabled={disabled || !prompt.trim()} />}</PromptInputToolbar>;
    $[9] = disabled;
    $[10] = isGenerating;
    $[11] = onStop;
    $[12] = prompt;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== handleSubmit || $[15] !== t2 || $[16] !== t5 || $[17] !== t7) {
    t8 = <div className="px-0 pb-0 sm:px-4"><div className="mx-auto w-full max-w-3xl bg-background px-0 pb-0 pt-2 sm:px-4"><PromptInput onSubmit={handleSubmit} aria-disabled={t2} className="[&_[data-slot=input-group]]:rounded-none [&_[data-slot=input-group]]:border-x-0 [&_[data-slot=input-group]]:border-b-0 sm:[&_[data-slot=input-group]]:rounded-md sm:[&_[data-slot=input-group]]:border">{t5}{t7}</PromptInput></div></div>;
    $[14] = handleSubmit;
    $[15] = t2;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
});
export default function CanvasPageClient({
  models,
  providers,
  organizations,
  selectedOrganization,
  selectedProject,
  projectsLoadFailed,
  initialModelPreference
}: CanvasPageClientProps) {
  const [selectedModel, setSelectedModel] = useState<string>(() => {
    const stored = getModelPreferenceCookie(CANVAS_MODEL_COOKIE) ?? initialModelPreference;
    if (stored) {
      return stored;
    }
    return DEFAULT_CANVAS_MODEL;
  });
  const [spec, setSpec] = useState<Spec>(emptySpec);
  const [editorValue, setEditorValue] = useState(EMPTY_SPEC_JSON);
  const [parseError, setParseError] = useState<string | null>(null);
  const [selectedTemplateName, setSelectedTemplateName] = useState<string>("");
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [promptResetKey, setPromptResetKey] = useState(0);
  const promptRef = useRef<HTMLTextAreaElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasStreamingSpec, setHasStreamingSpec] = useState(false);
  // The JSON spec is an advanced feature — hidden by default so the canvas
  // reads as prompt → live preview.
  const [showEditor, setShowEditor] = useState(false);
  const [previewExpanded, _setPreviewExpanded] = useState(false);
  const [exporting, setExporting] = useState<"pdf" | "image" | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const isMobile = useIsMobile();
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const handleSelectOrganization = useCallback((org: Organization | null) => {
    const params = new URLSearchParams(Array.from(searchParams.entries()));
    if (org?.id) {
      params.set("orgId", org.id);
    } else {
      params.delete("orgId");
    }
    params.delete("projectId");
    router.push(params.toString() ? `/canvas?${params.toString()}` : "/canvas");
  }, [router, searchParams]);
  const [isNewProjectOpen, setIsNewProjectOpen] = useState(false);
  const handleProjectCreated = useCallback((project: Project) => {
    if (!selectedOrganization) {
      return;
    }
    const params_0 = new URLSearchParams(Array.from(searchParams.entries()));
    params_0.set("orgId", selectedOrganization.id);
    params_0.set("projectId", project.id);
    router.push(`/canvas?${params_0.toString()}`);
  }, [router, searchParams, selectedOrganization]);
  const isAuthenticated = !isUserLoading && !!user;
  const showAuthDialog = !isAuthenticated && !isUserLoading && !user;
  useEffect(() => {
    if (selectedModel) {
      setModelPreferenceCookie(CANVAS_MODEL_COOKIE, selectedModel);
    }
  }, [selectedModel]);
  const keyQuery = useQuery({
    queryKey: ["canvas-playground-key", selectedProject?.id],
    enabled: isAuthenticated && !!selectedOrganization && !!selectedProject,
    staleTime: 0,
    queryFn: async ({
      signal
    }) => {
      const response = await fetch("/api/ensure-playground-key", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          projectId: selectedProject?.id
        }),
        signal
      });
      if (!response.ok) {
        throw new Error("Could not prepare your project for generation.");
      }
      return true;
    }
  });
  const generationReady = keyQuery.isSuccess && !keyQuery.isFetching;
  const handleEditorChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditorValue(e.target.value);
  }, []);
  const handleApply = useCallback(() => {
    try {
      const parsed = JSON.parse(editorValue) as Spec;
      if (!parsed.root || !parsed.elements) {
        setParseError("Spec must have 'root' and 'elements' fields");
        return;
      }
      setSpec(parsed);
      setParseError(null);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : "Invalid JSON");
    }
  }, [editorValue]);
  const handleEditorKeyDown = useCallback((e_0: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e_0.metaKey || e_0.ctrlKey) && e_0.key === "Enter") {
      e_0.preventDefault();
      handleApply();
    }
    if (e_0.key === "Tab") {
      e_0.preventDefault();
      const target = e_0.currentTarget;
      const start = target.selectionStart;
      const end = target.selectionEnd;
      const value = target.value;
      const newValue = value.substring(0, start) + "\t" + value.substring(end);
      setEditorValue(newValue);
      requestAnimationFrame(() => {
        target.selectionStart = target.selectionEnd = start + 1;
      });
    }
  }, [handleApply]);
  const applySpec = useCallback((nextSpec: Spec) => {
    setSpec(nextSpec);
    setEditorValue(JSON.stringify(nextSpec, null, 2));
    setParseError(null);
  }, []);
  const handleGenerate = useCallback(async (prompt: string) => {
    if (!prompt.trim() || isGenerating || !generationReady) {
      return;
    }
    const controller = new AbortController();
    setIsGenerating(true);
    setParseError(null);
    setSelectedTemplateName("");
    setHasStreamingSpec(false);
    abortRef.current = controller;
    try {
      const response_0 = await fetch("/api/canvas/generate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          prompt,
          model: selectedModel
        }),
        signal: controller.signal
      });
      if (!response_0.ok) {
        throw new Error(await getResponseErrorMessage(response_0));
      }
      const reader = response_0.body?.getReader();
      if (!reader) {
        throw new Error("No response stream");
      }
      const decoder = new TextDecoder();
      let specStreamBuffer = "";
      let didResetForStream = false;
      while (true) {
        const {
          done,
          value: value_0
        } = await reader.read();
        if (done) {
          break;
        }
        specStreamBuffer += decoder.decode(value_0, {
          stream: true
        });
        if (specStreamBuffer.trim()) {
          if (!didResetForStream) {
            setSpec(emptySpec);
            didResetForStream = true;
          }
          setHasStreamingSpec(true);
          setEditorValue(specStreamBuffer);
          try {
            const compiled = compileSpecStream(specStreamBuffer) as unknown;
            if (isRenderableSpec(compiled)) {
              applySpec(compiled);
            }
          } catch {
            // Spec not complete yet, continue
          }
        }
      }
      if (specStreamBuffer.trim()) {
        let appliedFinalSpec = false;
        try {
          const compiled_0 = compileSpecStream(specStreamBuffer) as unknown;
          if (isRenderableSpec(compiled_0)) {
            applySpec(compiled_0);
            setHasStreamingSpec(true);
            appliedFinalSpec = true;
          }
        } catch {
          // Fall back to JSON.parse below.
        }
        if (!appliedFinalSpec) {
          try {
            const parsed_0 = JSON.parse(specStreamBuffer) as unknown;
            if (isRenderableSpec(parsed_0)) {
              applySpec(parsed_0);
              setHasStreamingSpec(true);
            } else {
              setParseError("Failed to parse AI response as a valid spec");
            }
          } catch {
            setParseError("Failed to parse AI response as a valid spec");
          }
        }
      }
    } catch (err_0) {
      if (err_0 instanceof DOMException && err_0.name === "AbortError") {
        return;
      }
      toast.error(err_0 instanceof Error ? err_0.message : "Generation failed");
    } finally {
      if (abortRef.current === controller) {
        setIsGenerating(false);
        abortRef.current = null;
      }
    }
  }, [applySpec, isGenerating, selectedModel, generationReady]);
  const handleStop = useCallback(() => {
    abortRef.current?.abort();
    setIsGenerating(false);
  }, []);
  const handleTemplateSelect = useCallback((templateName: string) => {
    const template = templates.find(t => t.name === templateName);
    if (template) {
      const json = JSON.stringify(template.spec, null, 2);
      setEditorValue(json);
      setSpec(template.spec);
      setParseError(null);
      setSelectedTemplateName(templateName);
    }
  }, []);
  const handleExport = useCallback(async (format: "pdf" | "png") => {
    const node = previewRef.current;
    if (!node) {
      toast.error("Preview not available");
      return;
    }
    setExporting(format === "png" ? "image" : "pdf");
    try {
      const {
        toPng
      } = await import("html-to-image");
      const backgroundColor = window.getComputedStyle(document.body).backgroundColor;
      const dataUrl = await toPng(node, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor,
        width: node.scrollWidth,
        height: node.scrollHeight,
        style: {
          overflow: "visible",
          maxHeight: "none"
        }
      });
      if (format === "png") {
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = "canvas-export.png";
        a.click();
        toast.success("PNG exported");
        return;
      }
      const {
        jsPDF
      } = await import("jspdf");
      const pxW = node.scrollWidth * 2;
      const pxH = node.scrollHeight * 2;
      const ptW = pxW * (72 / 96);
      const ptH = pxH * (72 / 96);
      // eslint-disable-next-line new-cap
      const pdf = new jsPDF({
        orientation: ptW > ptH ? "landscape" : "portrait",
        unit: "pt",
        format: [ptW, ptH]
      });
      const rgb = backgroundColor.match(/\d+/g);
      if (rgb && rgb.length >= 3) {
        pdf.setFillColor(Number(rgb[0]), Number(rgb[1]), Number(rgb[2]));
        pdf.rect(0, 0, ptW, ptH, "F");
      }
      pdf.addImage(dataUrl, "PNG", 0, 0, ptW, ptH);
      pdf.save("canvas-export.pdf");
      toast.success("PDF exported");
    } catch {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    } finally {
      setExporting(null);
    }
  }, []);
  const hasSpec = useMemo(() => spec.root !== null && Object.keys(spec.elements).length > 0, [spec]);
  const isDefaultSpec = useMemo(() => JSON.stringify(spec) === EMPTY_SPEC_COMPACT_JSON, [spec]);
  const specStateKey = useMemo(() => JSON.stringify(spec.state ?? {}), [spec.state]);
  const showGenerationLoading = isGenerating && !hasStreamingSpec;
  const handleResetCanvas = useCallback(() => {
    setSpec(emptySpec);
    setEditorValue(EMPTY_SPEC_JSON);
    setParseError(null);
    setHasStreamingSpec(false);
    setSelectedTemplateName("");
    setPromptResetKey(key => key + 1);
    setShowResetDialog(false);
    setTimeout(() => promptRef.current?.focus(), 50);
  }, []);
  const handleNewCanvas = useCallback(() => {
    if (hasSpec && !isDefaultSpec) {
      setShowResetDialog(true);
    } else {
      promptRef.current?.focus();
    }
  }, [hasSpec, isDefaultSpec]);
  return <>
			<SidebarProvider>
				<div className="flex h-dvh w-full">
					<CanvasSidebar organizations={organizations} selectedOrganization={selectedOrganization} onSelectOrganization={handleSelectOrganization} onNewCanvas={handleNewCanvas} />
					<div className="flex min-w-0 flex-1 flex-col">
						{/* Header */}
						<header className="bg-background flex items-center border-b p-4">
							<div className="flex min-w-0 flex-1 items-center gap-3">
								<SidebarTrigger />
								<div className="flex w-full min-w-0 max-w-[360px] items-center gap-2 sm:max-w-[420px]">
									<ModelSelector models={models} providers={providers} value={selectedModel} onValueChange={setSelectedModel} placeholder="Search and select a model..." />
								</div>
							</div>
							<div className="ml-3 flex items-center gap-2">
								<Select value={selectedTemplateName} onValueChange={handleTemplateSelect}>
									<SelectTrigger className="h-9 w-auto min-w-[120px] max-w-[220px]">
										<div className="flex min-w-0 items-center gap-2 overflow-hidden">
											<LayoutTemplate className="h-4 w-4 shrink-0" />
											<span className="truncate text-sm">
												{selectedTemplateName || "Templates"}
											</span>
										</div>
									</SelectTrigger>
									<SelectContent>
										{templates.map(t_0 => <SelectItem key={t_0.name} value={t_0.name}>
												<div>
													<div className="font-medium">{t_0.name}</div>
													<div className="text-xs text-muted-foreground">
														{t_0.description}
													</div>
												</div>
											</SelectItem>)}
									</SelectContent>
								</Select>
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button variant="outline" disabled={exporting !== null || !hasSpec}>
											<Download className="h-4 w-4 sm:mr-2" />
											<span className="hidden sm:inline">Export</span>
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end">
										<DropdownMenuItem onClick={() => handleExport("pdf")}>
											<FileText className="mr-2 h-4 w-4" />
											Export as PDF
										</DropdownMenuItem>
										<DropdownMenuItem onClick={() => handleExport("png")}>
											<FileImage className="mr-2 h-4 w-4" />
											Export as PNG
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</div>
						</header>

						{/* Main content */}
						<div className="flex flex-1 overflow-hidden">
							{/* Editor Panel - Sheet on mobile, side column on desktop */}
							{isMobile ? <Sheet open={showEditor} onOpenChange={setShowEditor}>
									<SheetContent side="left" className="flex flex-col gap-0 p-0" aria-describedby={undefined}>
										<div className="flex items-center justify-between border-b px-4 py-2 pr-12">
											<SheetTitle className="text-sm font-medium text-muted-foreground">
												JSON Spec
											</SheetTitle>
											<div className="flex items-center gap-2">
												{parseError && <span className="max-w-[180px] truncate text-xs text-destructive">
														{parseError}
													</span>}
												<Button size="sm" variant="secondary" onClick={handleApply}>
													<Play className="mr-1.5 h-3 w-3" />
													Apply
												</Button>
											</div>
										</div>
										{showGenerationLoading ? <CanvasSpecSkeleton /> : <textarea value={editorValue} onChange={handleEditorChange} onKeyDown={handleEditorKeyDown} readOnly={isGenerating} spellCheck={false} className={`flex-1 resize-none bg-muted/30 p-4 font-mono text-xs leading-relaxed outline-none ${isGenerating ? "cursor-wait select-none" : ""}`} />}
									</SheetContent>
								</Sheet> : showEditor && !previewExpanded && <div className="flex w-[400px] flex-col border-r">
										<div className="flex items-center justify-between border-b px-4 py-2">
											<span className="text-sm font-medium text-muted-foreground">
												JSON Spec
											</span>
											<div className="flex items-center gap-2">
												{parseError && <span className="max-w-[180px] truncate text-xs text-destructive">
														{parseError}
													</span>}
												<Button size="sm" variant="secondary" onClick={handleApply}>
													<Play className="mr-1.5 h-3 w-3" />
													Apply
												</Button>
											</div>
										</div>
										{showGenerationLoading ? <CanvasSpecSkeleton /> : <textarea value={editorValue} onChange={handleEditorChange} onKeyDown={handleEditorKeyDown} readOnly={isGenerating} spellCheck={false} className={`flex-1 resize-none bg-muted/30 p-4 font-mono text-xs leading-relaxed outline-none ${isGenerating ? "cursor-wait select-none" : ""}`} />}
									</div>}

							{/* Preview Panel */}
							<div className="flex flex-1 flex-col">
								<div className="flex items-center justify-between border-b px-4 py-2">
									<div className="flex items-center gap-2">
										<span className="text-sm font-medium text-muted-foreground">
											Preview
										</span>
										{isGenerating && <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
												<Loader2 className="h-3 w-3 animate-spin" />
												Generating...
											</div>}
									</div>
									<div className="flex items-center gap-1">
										<Button variant={showEditor ? "secondary" : "ghost"} size="sm" onClick={() => setShowEditor(!showEditor)}>
											<Code className="h-3.5 w-3.5 sm:mr-1.5" />
											<span className="hidden sm:inline">
												{showEditor ? "Hide JSON" : "Show JSON"}
											</span>
										</Button>
										{/* Hidden: expand/collapse button is redundant for now */}
										{/* <Button
           variant="ghost"
           size="icon"
           className="h-8 w-8"
           onClick={() =>
           setPreviewExpanded(
           	!previewExpanded,
           )
           }
           >
           {previewExpanded ? (
           <Minimize2 className="h-3.5 w-3.5" />
           ) : (
           <Maximize2 className="h-3.5 w-3.5" />
           )}
           </Button> */}
									</div>
								</div>

								{/* Rendered preview */}
								<div ref={previewRef} className="flex-1 overflow-auto p-6">
									{showGenerationLoading ? <CanvasPreviewSkeleton /> : isDefaultSpec && !isGenerating ? <CanvasEmptyState disabled={!generationReady} onSuggestionClick={suggestion => {
                  void handleGenerate(suggestion);
                }} /> : <JSONUIProvider key={specStateKey} registry={registry} initialState={spec.state ?? {}}>
											<Renderer spec={spec} registry={registry} />
										</JSONUIProvider>}
								</div>

								{isAuthenticated && !selectedProject && <div role="alert" className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
										<span>
											{projectsLoadFailed ? "Could not load the projects for this organization." : "This organization has no project to generate with."}
										</span>
										{projectsLoadFailed || selectedOrganization?.kind === "chat" ? <Button variant="outline" size="sm" onClick={() => router.refresh()}>
												Reload
											</Button> : <div className="flex shrink-0 gap-2">
												<Button variant="outline" size="sm" onClick={() => setIsNewProjectOpen(true)}>
													Create project
												</Button>
												<Button variant="ghost" size="sm" onClick={() => handleSelectOrganization(null)}>
													Use Chat plan
												</Button>
											</div>}
									</div>}
								{selectedOrganization?.kind !== "chat" && <NewProjectDialog isOpen={isNewProjectOpen} setIsOpen={setIsNewProjectOpen} selectedOrganization={selectedOrganization} onProjectCreated={handleProjectCreated} />}
								{keyQuery.isError && <div role="alert" className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
										<span>Could not prepare your project for generation.</span>
										<Button variant="outline" size="sm" onClick={() => void keyQuery.refetch()}>
											Retry
										</Button>
									</div>}
								{/* Prompt input */}
								<CanvasPromptInput key={promptResetKey} disabled={!generationReady} isGenerating={isGenerating} onGenerate={prompt_0 => {
                void handleGenerate(prompt_0);
              }} onStop={handleStop} promptRef={promptRef} />
							</div>
						</div>
					</div>
				</div>
			</SidebarProvider>
			<Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Reset canvas?</DialogTitle>
						<DialogDescription>
							Your current canvas has content. Starting a new canvas will clear
							everything. This cannot be undone.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="outline" onClick={() => setShowResetDialog(false)}>
							Cancel
						</Button>
						<Button variant="destructive" onClick={handleResetCanvas}>
							Reset canvas
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			<AuthDialog open={showAuthDialog} returnUrl={pathname} />
		</>;
}